"use client";

import { ExternalLink } from "lucide-react";
import { AFFILIATES, type AffiliateKey } from "@/lib/affiliate-links";

// Map internal network names to short user-facing labels. Plain function
// (instead of inline ternary) sidesteps TypeScript's literal-narrowing
// when AFFILIATES `as const` collapses the union of `network` values down
// to the actual literals used — without it the unreachable-branch return
// type narrows to `never` and the build fails.
function networkLabel(n: string): string {
  if (n === "Amazon Associates") return "Affiliate";
  if (n === "Pending") return "Partner referral";
  return n;
}

/**
 * AffiliateLink — single component that renders any affiliate link
 * across the site with three guarantees:
 *
 * 1. **FTC compliance** — `rel="sponsored nofollow"` is always set.
 *    Pages displaying these must also include the
 *    <AffiliateDisclosureNotice> block per 16 CFR Part 255 (updated 2023).
 *
 * 2. **GA4 click attribution** — fires `affiliate_click` event with
 *    merchant, network, product key, and current page path. Lets you
 *    measure CTR per page and per merchant in GA4.
 *
 * 3. **Single source of truth** — pulls everything from AFFILIATES dict
 *    in lib/affiliate-links.ts. When tracking IDs arrive, you update
 *    one file; every page picks up the new tagged URL automatically.
 *
 * Two render styles:
 * - `card` (default) — full product card with name, description, CTA
 * - `inline` — a styled inline text link for prose passages
 */

type Props = {
  affiliateKey: AffiliateKey;
  variant?: "card" | "inline";
  /** Override CTA text on the card variant. */
  ctaLabel?: string;
  /** Override the rendered children for inline variant (defaults to entry.name). */
  children?: React.ReactNode;
};

export function AffiliateLink({
  affiliateKey,
  variant = "card",
  ctaLabel,
  children,
}: Props) {
  const entry = AFFILIATES[affiliateKey];

  function trackClick() {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (typeof w.gtag === "function") {
        w.gtag("event", "affiliate_click", {
          merchant: entry.merchant,
          network: entry.network,
          product_key: affiliateKey,
          page_path:
            typeof window !== "undefined" ? window.location.pathname : "",
        });
      }
    } catch {
      // Never break navigation on tracking error.
    }
  }

  if (variant === "inline") {
    return (
      <a
        href={entry.url}
        target="_blank"
        rel="sponsored nofollow noopener noreferrer"
        onClick={trackClick}
        style={{
          color: "#D4A84B",
          fontWeight: 600,
          textDecoration: "underline",
          textDecorationColor: "rgba(212,168,75,0.4)",
          textUnderlineOffset: "3px",
        }}
      >
        {children ?? entry.name}
        <ExternalLink
          size={12}
          style={{ display: "inline", marginLeft: 4, verticalAlign: "baseline" }}
        />
      </a>
    );
  }

  // Card variant — full product card
  return (
    <a
      href={entry.url}
      target="_blank"
      rel="sponsored nofollow noopener noreferrer"
      onClick={trackClick}
      style={{
        display: "flex",
        flexDirection: "column",
        background: "rgba(5,13,24,0.65)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        border: "1px solid rgba(255,255,255,0.18)",
        borderLeft: "3px solid #D4A84B",
        borderRadius: 14,
        padding: "20px 22px",
        textDecoration: "none",
        transition: "border-color 150ms, transform 150ms",
      }}
      className="hover:!border-l-amber-300 hover:translate-y-[-1px]"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <span
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 9,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: "rgba(212,168,75,0.8)",
            fontWeight: 700,
          }}
        >
          {entry.merchant}
        </span>
        <span
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 9,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.40)",
          }}
        >
          {networkLabel(entry.network)}
        </span>
      </div>
      <h3
        style={{
          color: "#ffffff",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 700,
          fontSize: "1.05rem",
          lineHeight: 1.3,
          marginBottom: 8,
        }}
      >
        {entry.name}
      </h3>
      <p
        style={{
          color: "rgba(255,255,255,0.78)",
          fontFamily: "'Barlow', sans-serif",
          fontSize: "0.88rem",
          lineHeight: 1.6,
          marginBottom: 16,
          flex: 1,
        }}
      >
        {entry.description}
      </p>
      <span
        style={{
          color: "#D4A84B",
          fontFamily: "'Barlow', sans-serif",
          fontSize: "0.82rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 1.5,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {ctaLabel ?? "View product"}
        <ExternalLink size={12} />
      </span>
    </a>
  );
}

/**
 * AffiliateDisclosureNotice — drop on any page that includes
 * <AffiliateLink>. FTC requires "clear and conspicuous" disclosure
 * placed *above* the recommendations, not in the footer.
 */
export function AffiliateDisclosureNotice() {
  return (
    <div
      style={{
        background: "rgba(212,168,75,0.07)",
        border: "1px solid rgba(212,168,75,0.25)",
        borderRadius: 10,
        padding: "14px 18px",
        margin: "0 0 24px",
        fontFamily: "'Barlow', sans-serif",
        fontSize: "0.82rem",
        lineHeight: 1.6,
        color: "rgba(255,255,255,0.80)",
      }}
    >
      <strong style={{ color: "#D4A84B" }}>Affiliate disclosure:</strong>{" "}
      Some links on this page are affiliate or partner-referral links. If you
      buy a product or sign up for a service through one, DRR may earn a small
      commission at no additional cost to you. We only recommend products our
      crews use, train on, or genuinely trust — see our{" "}
      <a
        href="/affiliate-disclosure"
        style={{
          color: "#D4A84B",
          fontWeight: 600,
          textDecoration: "underline",
          textUnderlineOffset: "2px",
        }}
      >
        full affiliate policy
      </a>{" "}
      for details.
    </div>
  );
}
